import React from 'react';
import {
  Chip,
  IconButton,
  Link,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Cancel, Delete, ErrorOutline } from '@material-ui/icons';
import { useTranslation } from '@michelin/central-provider';
import { PrimTechServiceAreaLocation, TechServiceAreaLocation } from '../types';

export const RenderShipToInfo = ({
  loc,
}: {
  loc: TechServiceAreaLocation;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <div style={{ minWidth: 170, paddingLeft: 8 }}>
      <Link component="span" underline="none" style={{ cursor: 'default' }}>
        <b>{loc.customer_dba_name || loc.customer_name || 'Mock name'}</b>
      </Link>
      <div>
        {`${t('Ship to #')} `}
        <b>{loc.customer_number || loc.ship_to_number || ''}</b>
      </div>
      {loc.extrnl_cust_id && (
        <div>
          {`${t('Store #')} `}
          <b>{loc.extrnl_cust_id}</b>
        </div>
      )}
    </div>
  );
};

export const RenderCity = ({
  loc,
}: {
  loc: TechServiceAreaLocation;
}): JSX.Element => {
  let country = (loc.customer_country || '').toUpperCase();
  switch (country) {
    case 'US':
      country = 'UNITED STATES';
      break;
    case 'CA':
      country = 'CANADA';
      break;
    case 'MX':
      country = 'MEXICO';
      break;
    default:
      break;
  }
  return (
    <React.Fragment key={`renderCity-${loc.ship_to_number}`}>
      {`${loc.customer_addr1 || loc.customer_addr2 || ''}`}
      <br />
      {`${loc.customer_city}, ${loc.customer_state} ${
        loc.customer_zip || ''
      }`}
      <br />
      {country}
    </React.Fragment>
  );
};

type RenderSecondaryProps = {
  data: PrimTechServiceAreaLocation;
  onDeleteClick: (primSt: string, secSt: string) => void;
  deleteDisabled: boolean;
};
const useStyles = makeStyles(() => ({
  tooltip12: {
    fontSize: '12px',
  },
  tooltip11: {
    fontSize: '11px',
  },
}));
export function RenderSecondary({
  data,
  onDeleteClick,
  deleteDisabled,
}: RenderSecondaryProps): JSX.Element {
  const classes = useStyles();
  const { t } = useTranslation();
  const empty = !data.secondary_st_locations || data.secondary_st_locations.length === 0;
  const secSorted = (data.secondary_st_locations || []).sort((a, b) => a.localeCompare(b));
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
      }}
    >
      {empty && (
        <Tooltip
          title={t('At least one Secondary ST Location is required').toString()}
          arrow
          placement="right-end"
          classes={{ tooltip: classes.tooltip12 }}
        >
          <ErrorOutline style={{ color: 'red' }} />
        </Tooltip>
      )}
      {secSorted.map((st) => (
        <Chip
          key={`${st}-${data.ship_to_number}`}
          label={`ST ${st}`}
          onDelete={
            deleteDisabled
              ? undefined
              : () => {
                onDeleteClick(data.ship_to_number, st);
              }
          }
          deleteIcon={<Cancel data-testid={`delete-${st}`} fontSize="small" />}
          color="primary"
          style={{
            borderRadius: 15,
            height: 30,
            marginRight: 8,
            marginBottom: 8,
          }}
        />
      ))}
    </div>
  );
}

type ActionsCellProps = {
  row: PrimTechServiceAreaLocation;
  action: 'edit' | 'view';
  onEditClick: (
    stNumber: string,
    locations: string[],
    primaryInfo: PrimTechServiceAreaLocation
  ) => void;
  onDeleteClick: (stNumber: string) => void;
  editDisabled: boolean;
  deleteDisabled: boolean;
};
export function ActionsCell({
  row,
  action,
  onEditClick,
  onDeleteClick,
  editDisabled = false,
  deleteDisabled = false,
}: ActionsCellProps): JSX.Element {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <div>
      <Tooltip
        title={t('Add / Remove Secondary ST Locations').toString()}
        arrow
        classes={{ tooltip: classes.tooltip11 }}
        disableHoverListener={action !== 'edit'}
      >
        <span>
          <IconButton
            aria-label="edit-secondary-locations"
            style={{ margin: '8px' }}
            onClick={() => onEditClick(
              row.ship_to_number,
              row.secondary_st_locations || [],
              row,
            )}
            size="small"
            disabled={editDisabled}
          >
            <Typography
              variant="body1"
              style={{
                fontWeight: 'bold',
                color: editDisabled ? 'gray' : '#3f51b5',
              }}
            >
              + / –
            </Typography>
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip
        title={t('Remove Technician Service Area').toString()}
        arrow
        classes={{ tooltip: classes.tooltip11 }}
        disableHoverListener={action !== 'edit'}
      >
        <span>
          <IconButton
            aria-label="delete"
            style={{ margin: '8px' }}
            onClick={async () => {
              onDeleteClick(row.ship_to_number);
            }}
            size="small"
            disabled={deleteDisabled}
          >
            <Delete
              fontSize="small"
              color={deleteDisabled ? 'disabled' : 'primary'}
              data-testid="DeleteIcon"
            />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}
